import { Collapse } from 'src/design/atoms/Collapse';
import { Group } from 'src/design/atoms/Group';
import { LoadingOverlay } from 'src/design/atoms/LoadingOverlay';
import { Paper } from 'src/design/atoms/Paper';
import { SimpleGrid } from 'src/design/atoms/SimpleGrid';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import { Radio } from 'src/design/atoms/Radio';
import { TextInput } from 'src/design/atoms/TextInput';
import { useFormik } from 'formik';
import isEqual from 'lodash/isEqual';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MeetingDto,
  MeetingTopicDto,
  MeetingTypeDto,
  UserDto,
  DocumentVersionDto,
} from 'src/api/FiretrolApiClient';
import { useDocument } from 'src/api/queries/documents/useDocument';
import { useUpdateMeeting } from 'src/api/queries/meetings/useMeetingMutations';
import { useCreateMeetingTopic } from 'src/api/queries/meetings/useMeetingTopicMutations';
import * as Yup from 'yup';

import { useDisclosure } from '@mantine/hooks';
import { JobUserSelect2 as MultiJobUserSelect2 } from 'src/routes/Jobs/MeetingMinutes/MeetingForm/JobUserSelect2';
import { MeetingTypeSelect } from 'src/routes/Jobs/MeetingMinutes/MeetingForm/MeetingTypeSelect';
import { showSuccessToast } from 'src/utils';
import { ViewActionIcon } from 'src/design/atoms/ViewActionIcon';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { SaveButton } from 'src/design/atoms/SaveButton';
import { DatePicker } from 'src/design/atoms/DatePicker';
import { DocumentVersionTable } from 'src/app/documents/DocumentVersionTable';
import { Topic } from './components/Topic';
import { mergeMeetingSnapshot } from './meetingSnapshot';
import { PendingEditsProvider, usePendingEditsTracker } from './pendingEdits';

const validationSchema = Yup.object().shape({
  title: Yup.string().required('Title is required'),
  meetingType: Yup.object().required('Meeting type is required'),
  status: Yup.string().required('Status is required'),
  startDate: Yup.mixed().required().label('Date'),
  endDate: Yup.mixed().required().label('Date'),
});

// Each row's updatedAt only moves forward, so the stamps are compared row by
// row: a read is older than the one on screen only when a row it still carries
// is stamped before the copy already applied. A single newest-in-the-tree
// number is not an ordering: deleting a topic lowers it and would make every
// later read look stale.
type RowVersions = ReadonlyMap<string, number>;

const rowVersions = (meeting: MeetingDto): RowVersions => {
  const versions = new Map<string, number>();
  const rows: [string | undefined, Date | undefined][] = [
    [meeting.id, meeting.updatedAt],
    ...(meeting.topics ?? []).map(
      (topic): [string | undefined, Date | undefined] => [
        topic.id,
        topic.updatedAt,
      ],
    ),
  ];
  rows.forEach(([id, stamp]) => {
    if (!id || stamp == null) return;
    versions.set(id, new Date(stamp).getTime());
  });
  return versions;
};

const predatesApplied = (next: RowVersions, applied: RowVersions) =>
  [...next].some(([id, version]) => {
    const seen = applied.get(id);
    return seen != null && version < seen;
  });

const mergeVersions = (
  applied: RowVersions,
  next: RowVersions,
): RowVersions => {
  const merged = new Map(applied);
  next.forEach((version, id) =>
    merged.set(id, Math.max(merged.get(id) ?? 0, version)),
  );
  return merged;
};

interface IMeetingFormProps {
  initialValues: MeetingDto;
  onMeetingTypeUpdated: (type: string) => void;
  jobId: number;
}

export const MeetingForm: React.FC<IMeetingFormProps> = ({
  initialValues,
  jobId,
  onMeetingTypeUpdated,
}) => {
  const navigate = useNavigate();
  const [showLoader, setShowLoader] = React.useState(false);
  const [versionsExpanded, { toggle: toggleVersionsExpanded }] =
    useDisclosure(false);

  const updateMeeting = useUpdateMeeting(jobId);
  const createTopic = useCreateMeetingTopic(jobId, initialValues.id);
  const loading = updateMeeting.isPending || createTopic.isPending;

  React.useEffect(() => {
    setShowLoader(true);
    if (!loading) {
      const handler = setTimeout(() => {
        setShowLoader(false);
      }, 1000);
      return () => {
        clearTimeout(handler);
      };
    }
  }, [loading]);

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnMount: true,
    onSubmit: (_values, { setSubmitting }) => {
      showSuccessToast(`Meeting minute created!`);
      navigate(`/jobs/${jobId}/meetings`);
      setSubmitting(false);
    },
  });

  const { values, setFieldValue, dirty, touched, errors, isValid, resetForm } =
    formik;

  // The server owns the topic/issue tree, so a refetched meeting replaces the
  // Formik tree — but only when it actually differs, or every autosave round
  // trip would wipe whatever the user is typing.
  const valuesRef = React.useRef(values);
  valuesRef.current = values;

  // A debounced topic/issue editor holds input that has not reached Formik
  // yet; resetting the tree under it drops that input.
  const { hasPendingEdits, setPending } = usePendingEditsTracker();

  // Only a snapshot the form has not seen yet may reseed it, and never one that
  // predates the tree already applied. A debounced editor flushing into Formik
  // clears its pending mark and re-runs this effect while the snapshot still
  // predates that edit, and a meeting read started before the edit can land
  // after the one that carries it; reseeding from either rolls the edit back
  // and the next topic write sends the stale value to the server.
  const lastAppliedValues = React.useRef(initialValues);
  const appliedVersions = React.useRef(rowVersions(initialValues));

  React.useEffect(() => {
    if (hasPendingEdits) return;
    const versions = rowVersions(initialValues);
    if (predatesApplied(versions, appliedVersions.current)) return;
    if (isEqual(initialValues, lastAppliedValues.current)) return;
    const applied = lastAppliedValues.current;
    lastAppliedValues.current = initialValues;
    appliedVersions.current = mergeVersions(appliedVersions.current, versions);
    if (isEqual(initialValues, valuesRef.current)) return;
    resetForm({
      values: mergeMeetingSnapshot(initialValues, valuesRef.current, applied),
    });
  }, [initialValues, hasPendingEdits, resetForm]);

  const exportDocument = useDocument(values.jobId, values.exportId);

  const versions: DocumentVersionDto[] = React.useMemo(() => {
    const result = exportDocument.data;
    if (!result) return [];
    return [
      {
        id: result.id,
        updatedAt: result.updatedAt,
        uploadedBy: result.uploadedBy,
      },
      ...result.versions,
    ];
  }, [exportDocument.data]);

  const saveMeetingUpdates = () =>
    updateMeeting.mutate({
      meetingId: values.id,
      request: values,
    });

  const addTopic = () =>
    createTopic.mutate(undefined, {
      onSuccess: (result) =>
        setFieldValue('topics', [
          ...(values.topics ?? []),
          { ...result, attachments: result.attachments ?? [] },
        ]),
    });

  const onTopicChange = (topic: MeetingTopicDto) => {
    const updatedTopics = (values.topics ?? []).map((t) => {
      if (t.id === topic.id) {
        return {
          ...topic,
          attachments: topic.attachments ?? [],
        };
      }
      return t;
    });
    setFieldValue('topics', updatedTopics);
  };

  const onTopicDeleted = (topic: MeetingTopicDto) => {
    const updatedTopics = (values.topics ?? []).filter(
      (t) => t.id !== topic.id,
    );
    setFieldValue('topics', updatedTopics);
  };

  return (
    <PendingEditsProvider value={setPending}>
      <Stack gap="md" pos="relative">
        <LoadingOverlay visible={showLoader} />
        <Paper>
          <Stack gap="md">
            <SimpleGrid cols={2}>
              <TextInput
                label="Meeting Title"
                placeholder="Enter meeting title"
                value={values.title}
                onChange={(e) => {
                  setFieldValue('title', e.target.value);
                  onMeetingTypeUpdated(e.target.value);
                }}
                onBlur={formik.handleBlur}
                error={touched.title && errors.title}
                required
              />

              <MeetingTypeSelect
                required
                jobId={jobId}
                onChange={(value: MeetingTypeDto | null) => {
                  setFieldValue('meetingType', value);
                }}
                value={values.meetingType}
              />
            </SimpleGrid>

            <SimpleGrid cols={2}>
              <DatePicker
                label="Start Date"
                showNow
                onChange={(value) => setFieldValue('startDate', value)}
                value={values.startDate}
                enableTime
                required
              />
              <DatePicker
                label="End Date"
                showNow
                onChange={(value) => setFieldValue('endDate', value)}
                value={values.endDate}
                enableTime
                required
              />
            </SimpleGrid>

            <SimpleGrid cols={2}>
              <MultiJobUserSelect2
                label="Administrators"
                placeholder="Select admins..."
                jobId={jobId}
                value={values.admins}
                customValues={values.customAdmins}
                onCustomChange={(users) => {
                  if (users) {
                    setFieldValue('customAdmins', users);
                  } else {
                    setFieldValue('customAdmins', []);
                  }
                }}
                onChange={(value: UserDto[]) => {
                  if (value) {
                    setFieldValue('admins', value);
                  } else {
                    setFieldValue('admins', []);
                  }
                }}
              />
              <MultiJobUserSelect2
                label="Leads"
                placeholder="Select leads..."
                jobId={jobId}
                value={values.leads}
                customValues={values.customLeads}
                onCustomChange={(users) => {
                  if (users) {
                    setFieldValue('customLeads', users);
                  } else {
                    setFieldValue('customLeads', []);
                  }
                }}
                onChange={(value: UserDto[]) => {
                  if (value) {
                    setFieldValue('leads', value);
                  } else {
                    setFieldValue('leads', []);
                  }
                }}
              />
            </SimpleGrid>

            <SimpleGrid cols={2}>
              <MultiJobUserSelect2
                label="Invitees"
                placeholder="Select invitees..."
                jobId={jobId}
                value={values.invitees}
                customValues={values.customInvitees}
                onCustomChange={(users) => {
                  if (users) {
                    setFieldValue('customInvitees', users);
                  } else {
                    setFieldValue('customInvitees', []);
                  }
                }}
                onChange={(users: UserDto[]) => {
                  if (users) {
                    setFieldValue('invitees', users);
                  } else {
                    setFieldValue('invitees', []);
                  }
                }}
              />
              <MultiJobUserSelect2
                label="Attendees"
                placeholder="Select attendees..."
                jobId={jobId}
                value={values.attendees}
                customValues={values.customAttendees}
                onCustomChange={(users) => {
                  if (users) {
                    setFieldValue('customAttendees', users);
                  } else {
                    setFieldValue('customAttendees', []);
                  }
                }}
                onChange={(users: UserDto[]) => {
                  if (users) {
                    setFieldValue('attendees', users);
                  } else {
                    setFieldValue('attendees', []);
                  }
                }}
              />
            </SimpleGrid>

            <Radio.Group
              label="Status"
              required
              value={values.status}
              onChange={(value: 'Draft' | 'Published') =>
                setFieldValue('status', value)
              }
              error={touched.status && errors.status}
            >
              <Group mt="xs">
                <Radio value="Draft" label="Draft" />
                <Radio value="Published" label="Published" />
              </Group>
            </Radio.Group>

            <Group justify="flex-end" mt="md">
              <SaveButton
                onClick={saveMeetingUpdates}
                disabled={!isValid || !dirty}
                loading={loading}
              >
                Save Meeting
              </SaveButton>
            </Group>
          </Stack>
        </Paper>

        <Paper>
          <Stack gap="md">
            <Group>
              <Title order={4}>Published Versions</Title>
              <ViewActionIcon
                onClick={toggleVersionsExpanded}
                variant={versionsExpanded ? 'filled' : 'light'}
              />
            </Group>
            <Text size="xs">{`Versions: ${versions.length}`}</Text>

            <Collapse in={versionsExpanded}>
              <DocumentVersionTable
                emptyText="Meeting has not been published"
                documents={versions}
                jobId={values.jobId}
                selectedDocumentId={values.exportId ?? null}
                hasLatest
              />
            </Collapse>
          </Stack>
        </Paper>

        <Stack gap="md">
          {(values.topics ?? []).map((topic) => {
            return (
              <Topic
                key={topic.id}
                topic={topic}
                onTopicChange={onTopicChange}
                onTopicDeleted={onTopicDeleted}
              />
            );
          })}
          <Group justify="center">
            <CreateButton onClick={addTopic}>Add Topic</CreateButton>
          </Group>
        </Stack>
      </Stack>
    </PendingEditsProvider>
  );
};
